import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { captureException } from '@sentry/nextjs'

import appConfig from '~/config/app'
import useSsrSafeValue from '~/hooks/useSsrSafeValue'
import { useAppDispatch } from '~/redux/hooks'
import { UserField } from '~/types/user'
import { setFieldValueActionCreator } from '~/settings-scenario/redux/settings.slice'
import { CurriculumEvents } from '~/analytics/curriculum/curriculumEvents.types'
import curriculumAnalytics from '~/analytics/curriculum/curriculumAnalytics'
import MenuLanguageDropdown from '~/curriculum/components/MenuLanguageDropdown'
import useCurrentUser from '~/hooks/useCurrentUser'
import useReloadPage from '~/hooks/useReloadPage'
import { useUpdateUserMutation } from '~/redux/apis/user-api'

import { setCurriculumName } from '../slice/persistCurriculum.slice'
import { CurriculumCountryCodes, CurriculumSelectionPoints } from '../constants/curriculum.const'
import { getStateCodeForCurriculumName, isDisplayOnlyCurriculumChange } from '../constants/usCurriculumStates.const'
import { useResolvedUsCurriculumState } from '../hooks/useResolvedUsCurriculumState'
import {
  useGetCurriculumCountryCodeFromUrlWithoutPrefix,
  useGetCurriculumNameFromCurriculumCountryCode,
  useGetInCurriculumV2,
  useGetUserChosenCurriculumCountryCode,
} from '../hooks/useGetCurriculumCountryCode'
import {
  closeCurriculumDropdownsActionCreator,
  setCurriculumChangeWarningActionCreator,
} from '../slice/curriculum.slice'

import OverviewCurriculumDropdown from './OverviewCurriculumDropdown'
import FooterCurriculumList from './FooterCurriculumList'
import MenuCurriculumDropdown from './MenuCurriculumDropdown'

interface Props {
  selectionPoint: CurriculumSelectionPoints
  theme?: 'light' | 'dark'
  hideTitle?: boolean
  isLightNested?: boolean
}
export default function CurriculumSelection({
  selectionPoint,
  theme = 'light',
  hideTitle,
  isLightNested,
}: Props): React.ReactElement | null {
  const usersCurriculumCountryCode = useGetUserChosenCurriculumCountryCode()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const curriculumCountryCodeFromUrl = useGetCurriculumCountryCodeFromUrlWithoutPrefix()
  // The chosen country comes from a cookie the server cannot see, so it has to render its empty
  // server value until mount or hydration mismatches.
  const ssrSafeCountryCode = useSsrSafeValue('', usersCurriculumCountryCode)
  const curriculumCountryCodeToShow =
    selectionPoint === CurriculumSelectionPoints.SELECT_GRADE ? curriculumCountryCodeFromUrl : ssrSafeCountryCode

  const isCurriculumV2 = useGetInCurriculumV2()
  const fallbackCurriculumName = useGetCurriculumNameFromCurriculumCountryCode(curriculumCountryCodeToShow)
  const user = useCurrentUser()
  const [updateUser] = useUpdateUserMutation()
  const reloadPage = useReloadPage()
  const { t } = useTranslation('curriculum')
  const { state: usCurriculumState, isUnset } = useResolvedUsCurriculumState()
  // Resolved from the account, localStorage and a geo lookup, so it needs the same guard as the country.
  const ssrSafeStateName = useSsrSafeValue('', usCurriculumState?.curriculumName ?? '')
  const ssrSafeIsUnset = useSsrSafeValue(true, isUnset)

  // Single chokepoint for all four switching surfaces: a school seat's curriculum comes from the
  // roster, never from the student. Placed after the hooks so the hook order stays unconditional.
  if (appConfig.isSchoolPlatform) return null

  // Nothing chosen must match no row, or the catch-all is highlighted and disabled — locking them out of it.
  const userCurriculumName =
    curriculumCountryCodeToShow !== CurriculumCountryCodes.USA
      ? fallbackCurriculumName
      : ssrSafeIsUnset
      ? ''
      : ssrSafeStateName || fallbackCurriculumName
  const unsetStateLabel =
    curriculumCountryCodeToShow === CurriculumCountryCodes.USA && ssrSafeIsUnset ? t('selectCurriculum') : undefined

  const selectionSurface =
    selectionPoint === CurriculumSelectionPoints.SELECT_GRADE
      ? 'select_grade'
      : selectionPoint === CurriculumSelectionPoints.FOOTER
      ? 'footer'
      : 'menu'

  const handleOptionSelect = (chosenCurriculumName: string): void => {
    curriculumAnalytics.logCurriculumClick(
      CurriculumEvents.CURRICULUM_COUNTRY_SELECTED,
      { curriculum_name: chosenCurriculumName, surface: selectionSurface },
      isCurriculumV2 ? 'v2' : 'v1',
    )
    // Same 'us' content under another name: no warning, no grade reset. The modal path saves the state via settings.
    if (isDisplayOnlyCurriculumChange(chosenCurriculumName, usersCurriculumCountryCode)) {
      const curriculumStateCode = getStateCodeForCurriculumName(chosenCurriculumName)
      dispatch(setCurriculumName(chosenCurriculumName))
      // Close the dropdown since no modal will be shown
      dispatch(closeCurriculumDropdownsActionCreator())
      if (!user?.id || !curriculumStateCode) {
        router.push(router.asPath)
        return
      }
      // Same content, so a real reload is the only sign of the switch; select-grade stays soft for onboarding.
      const shouldReload = selectionPoint !== CurriculumSelectionPoints.SELECT_GRADE
      updateUser({ data: { curriculumStateCode } })
        .unwrap()
        .then(async () => {
          if (shouldReload) await reloadPage()
          else await router.push(router.asPath)
        })
        .catch((error) => {
          captureException(error, { tags: { feature: 'curriculum', action: 'save-state' } })
          router.push(router.asPath)
        })
      return
    }

    // For all other cases, first update the settings state with the chosen curriculum name
    // so the warning modal knows what curriculum to change to when user clicks "continue"
    dispatch(setFieldValueActionCreator({ fieldName: UserField.CURRICULUM, value: chosenCurriculumName }))

    // Then show warning modal
    dispatch(setCurriculumChangeWarningActionCreator(true))
  }

  switch (selectionPoint) {
    case CurriculumSelectionPoints.SELECT_GRADE:
      return (
        <OverviewCurriculumDropdown
          currentCurriculum={userCurriculumName}
          currentCurriculumCode={curriculumCountryCodeToShow}
          handleOptionSelect={handleOptionSelect}
          isLightNested={isLightNested}
          unsetStateLabel={unsetStateLabel}
        />
      )

    case CurriculumSelectionPoints.FOOTER:
      return (
        <FooterCurriculumList
          currentCurriculum={userCurriculumName}
          currentCurriculumCode={curriculumCountryCodeToShow}
          handleOptionSelect={handleOptionSelect}
          hideTitle={hideTitle}
          unsetStateLabel={unsetStateLabel}
        />
      )

    case CurriculumSelectionPoints.HAMBURGER_MENU:
      return (
        <MenuCurriculumDropdown
          currentCurriculum={userCurriculumName}
          currentCurriculumCode={curriculumCountryCodeToShow}
          handleOptionSelect={handleOptionSelect}
          theme={theme}
          unsetStateLabel={unsetStateLabel}
        />
      )
    case CurriculumSelectionPoints.CAMPAIGN_PAGE:
      return (
        <MenuLanguageDropdown
          currentCurriculum={userCurriculumName}
          currentCurriculumCode={curriculumCountryCodeToShow}
          handleOptionSelect={handleOptionSelect}
        />
      )
  }
}
